import type { TFunction } from 'i18next';

import type { OnboardingDraft, OnboardingErrors } from '../types';

export const ONBOARDING_URL_PATTERN = /^https?:\/\/[^\s]+$/i;

/** Validates a single onboarding wizard step — shared by OnboardingWizard and ProfilePage. */
export function validateOnboardingStep(
  step: number,
  draft: OnboardingDraft,
  t: TFunction,
): OnboardingErrors {
  const e: OnboardingErrors = {};

  if (step === 1) {
    if (!draft.displayName.trim()) {
      e.displayName = t('onboarding.validation.displayNameRequired', 'Please tell us your name.');
    } else if (draft.displayName.trim().length < 2) {
      e.displayName = t('onboarding.validation.displayNameTooShort', 'Name is too short.');
    }

    if (!draft.currentRole.trim()) {
      e.currentRole = t('onboarding.validation.currentRoleRequired', "What's your current job title?");
    }

    if (!draft.experienceYears.trim()) {
      e.experienceYears = t('onboarding.validation.experienceYearsRequired', 'Please enter your years of experience.');
    } else {
      const n = Number(draft.experienceYears);
      if (!Number.isFinite(n) || n < 0 || n > 60) {
        e.experienceYears = t('onboarding.validation.experienceYearsRange', 'Enter a number between 0 and 60.');
      }
    }

    if (draft.headline.trim() && draft.headline.trim().length < 8) {
      e.headline = t('onboarding.validation.headlineTooShort', 'Headline is too short — or leave it blank for now.');
    }
  }

  if (step === 2) {
    if (draft.skills.length < 3) {
      e.skills = t('onboarding.validation.skillsMin', 'Add at least 3 skills.');
    }
  }

  if (step === 3) {
    if (!draft.industry) {
      e.industry = t('onboarding.validation.industryRequired', 'Pick the closest industry.');
    }
    if (!draft.jobSearchStatus) {
      e.jobSearchStatus = t('onboarding.validation.jobSearchStatusRequired', "Tell us where you're at.");
    }
    if (draft.preferredJobTypes.length === 0) {
      e.preferredJobTypes = t('onboarding.validation.preferredJobTypesRequired', 'Pick at least one preferred job type.');
    }
    if (draft.salaryExpectationInr.trim()) {
      const n = Number(draft.salaryExpectationInr);
      if (!Number.isFinite(n) || n < 0) {
        e.salaryExpectationInr = t('onboarding.validation.salaryInvalid', 'Enter a positive number, or leave it blank.');
      }
    }
  }

  if (step === 4) {
    const urlError = t('onboarding.validation.urlInvalid', 'Use the full URL, including https://');
    if (draft.linkedinUrl.trim() && !ONBOARDING_URL_PATTERN.test(draft.linkedinUrl.trim())) {
      e.linkedinUrl = urlError;
    }
    if (draft.githubUrl.trim() && !ONBOARDING_URL_PATTERN.test(draft.githubUrl.trim())) {
      e.githubUrl = urlError;
    }
    if (draft.portfolioUrl.trim() && !ONBOARDING_URL_PATTERN.test(draft.portfolioUrl.trim())) {
      e.portfolioUrl = urlError;
    }
  }

  return e;
}
